import {
  CONFIG_CLASS_ID,
  TConfigFieldNameId,
  TConfigAdditionalActionId,
  TProductConfigAdditionalAction,
  TConfigEvidence,
  CONFIG_EVIDENCE_FIELD_NAME_ID,
  EVIDENCE_FIELD_NAME,
  CONFIG_STATUS_ID,
  CONFIG_STATUS_NAME
} from '../types/product.configuration.types';
import { PRODUCT_CATEGORY } from '../types/category';
import {
  CONFIG_FIELD_NAME_TO_ID,
  TConfigFieldNameToId,
  CONFIG_CLASS_NAME_TO_ID,
  TConfigClassNameToId,
  CONFIG_CLASS,
  IConfigClass,
  TRANSLATED_CONFIGURATION_CLASS_NAMES,
  TRANSLATED_CONFIGURATION_FIELD_NAMES,
  TConfigFieldName,
  CONFIG_CLASS_REQUIRED_ADDITIONAL_ACTIONS,
  TConfigClassRequiredAdditionalActions
} from '../constants/configurations.constants';

/**
 * Función utilitaria para obtener la clase de configuración
 * @param configClassNameOrId - Nombre o ID de la clase de configuración
 * @returns Clase de configuración o null si no se encuentra
 */
export const getConfigClass = (configClassNameOrId: string | number | null | undefined): IConfigClass | null => {
  if (!configClassNameOrId) {
    return null;
  }
  if (typeof configClassNameOrId === 'number') {
    return CONFIG_CLASS[configClassNameOrId as CONFIG_CLASS_ID] || null;
  }
  const configClassId = CONFIG_CLASS_NAME_TO_ID[configClassNameOrId as TConfigClassNameToId];
  return CONFIG_CLASS[configClassId as CONFIG_CLASS_ID] || null;
};

/**
 * Categorías de productos que pueden tener configuraciones adicionales
 */
const CONFIGURABLE_PRODUCT_CATEGORIES = [
  PRODUCT_CATEGORY.LAPTOP,
  PRODUCT_CATEGORY.CTO_LAPTOPS,
  PRODUCT_CATEGORY.DEP_LAPTOPS
];

/**
 * Verifica si un producto es configurable (puede tener configuraciones adicionales)
 * @param category - Categoría del producto
 * @returns true si el producto es configurable, false en caso contrario
 */
export const isProductConfigurable = (category: PRODUCT_CATEGORY | string | null | undefined): boolean => {
  if (!category) return false;
  return CONFIGURABLE_PRODUCT_CATEGORIES.includes(category as PRODUCT_CATEGORY);
};

/**
 * Función utilitaria para obtener el nombre legible de una clase de configuración
 * @param classNameOrId - Nombre (clave) o id (valor) de la clase que viene de la base de datos
 * @returns Nombre legible en español o 'Sin información' si no se encuentra
 */
export const getConfigClassDisplayName = (classNameOrId: string | number | null | undefined): string => {
  const configClass = getConfigClass(classNameOrId);
  if (!configClass) {
    return 'Sin información';
  }
  return configClass.name;
};

/**
 * Función utilitaria para obtener el nombre legible de un campo de configuración.
 * @param configClassNameOrId - Nombre o ID de la clase de configuración
 * @param fieldNameOrId - Nombre o ID del campo que viene de la base de datos
 * @returns Nombre legible en español, nombre original o 'Sin información' si no se encuentra
 */
export const getConfigFieldDisplayName = (
  configClassNameOrId: string | number | null | undefined,
  fieldNameOrId: string | number | null | undefined
): string => {
  const configClass = getConfigClass(configClassNameOrId);
  if (!configClass || !fieldNameOrId) {
    return 'Sin información';
  }

  const fieldId =
    typeof fieldNameOrId === 'number'
      ? (fieldNameOrId as TConfigFieldNameId)
      : CONFIG_FIELD_NAME_TO_ID[fieldNameOrId as TConfigFieldNameToId];

  const field = configClass.fields?.find(field => field.id === fieldId);
  return field?.label || String(fieldNameOrId);
};

/**
 * Función utilitaria para obtener una acción adicional específica del array de acciones adicionales
 * @param additionalActions - Array de acciones adicionales (nuevo formato unificado)
 * @param actionId - ID de la acción a buscar
 * @returns La acción adicional si existe, null en caso contrario
 */
export const getAdditionalAction = (
  additionalActions: TProductConfigAdditionalAction[] | null | undefined,
  actionId: TConfigAdditionalActionId
): TProductConfigAdditionalAction | null => {
  if (!additionalActions) return null;
  return additionalActions.find(action => action.actionId === actionId) || null;
};

/**
 * Función utilitaria para verificar si todas las acciones adicionales están confirmadas
 * @param additionalActions - Array de acciones adicionales
 * @returns true si todas las acciones están confirmadas o en caso de que el array no exista o esté vacio, false en caso contrario
 */
export const checkAllActionsConfirmed = (
  additionalActions: TProductConfigAdditionalAction[] | null | undefined
): boolean => {
  if (!additionalActions || additionalActions.length === 0) return true;
  return additionalActions.every(action => action.confirmedAt !== null && action.confirmedAt !== undefined);
};

/**
 * Función utilitaria para verificar si una configuración requiere una acción adicional
 * @param configClassName - Nombre de la clase de configuración
 * @returns true si la configuración requiere una acción adicional, false en caso contrario
 */
export const hasConfigRequiredAdditionalAction = (configClassId?: number | null | undefined): boolean => {
  if (!configClassId) return false;
  return CONFIG_CLASS_REQUIRED_ADDITIONAL_ACTIONS.includes(configClassId as TConfigClassRequiredAdditionalActions);
};

export const getI18nKeySafe = (configurationClassId?: number | null, fieldName?: string | null): string => {
  const defaultKey = 'unknownField';
  if (!configurationClassId || !fieldName) return defaultKey;

  const configClassKey = TRANSLATED_CONFIGURATION_CLASS_NAMES[configurationClassId as CONFIG_CLASS_ID];
  if (!configClassKey) return defaultKey;

  const configFieldKey = TRANSLATED_CONFIGURATION_FIELD_NAMES[fieldName as TConfigFieldName];
  if (!configFieldKey) return defaultKey;

  return `${configClassKey}.${configFieldKey}`;
};

export const getFileUrlsFromEvidence = (evidence: TConfigEvidence[]): string[] => {
  return (
    evidence
      ?.filter(e => e.name === EVIDENCE_FIELD_NAME[CONFIG_EVIDENCE_FIELD_NAME_ID.FILE_URL])
      .map(e => e.evidence) || []
  );
};

export const getNotCompletedReasonIdFromEvidence = (evidence: TConfigEvidence[]): number | undefined => {
  const reasonEvidence = evidence?.find(
    e => e.name === EVIDENCE_FIELD_NAME[CONFIG_EVIDENCE_FIELD_NAME_ID.NOT_COMPLETED_REASON]
  );
  return reasonEvidence ? parseInt(reasonEvidence.evidence, 10) || undefined : undefined;
};

export const getCommentsFromEvidence = (evidence: TConfigEvidence[]): string | undefined => {
  return evidence?.find(e => e.name === EVIDENCE_FIELD_NAME[CONFIG_EVIDENCE_FIELD_NAME_ID.COMMENTS])?.evidence;
};

export const getStatusNameFromId = (statusId: number): CONFIG_STATUS_NAME => {
  if (statusId === CONFIG_STATUS_ID.TO_COMPLETE) return CONFIG_STATUS_NAME.TO_COMPLETE;
  if (statusId === CONFIG_STATUS_ID.COMPLETED) return CONFIG_STATUS_NAME.COMPLETED;
  if (statusId === CONFIG_STATUS_ID.NOT_COMPLETED) return CONFIG_STATUS_NAME.NOT_COMPLETED;
  return CONFIG_STATUS_NAME.TO_COMPLETE;
};
